import { getInitialPageFromDOM, type Page } from '@inertiajs/core'
import { createInertiaApp, Link } from '@inertiajs/vue3'
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers'
import {
  h,
  type App as VueApp,
  type CreateAppFunction,
  type DefineComponent,
  type Plugin,
} from 'vue'
import { ZiggyVue } from 'ziggy-js'

import { loadLanguageFile } from './i18n'

export type ZiggyConfig = NonNullable<Parameters<typeof ZiggyVue.install>[1]>

type InertiaSetupOptions = {
  el: Element | null
  App: unknown
  props: unknown
  plugin: Plugin
}

declare global {
  // Ziggy resolves imported `route()` calls from this global outside Vue plugin injection.
  // eslint-disable-next-line no-var
  var Ziggy: ZiggyConfig | undefined
}

export async function createFrankfurtPlusApp({
  appFactory,
  page,
  render,
  enhance,
}: {
  appFactory: CreateAppFunction<Element>
  page?: Page
  render?: (app: VueApp) => Promise<string>
  enhance?: (app: VueApp) => void
}) {
  const initialPage = page ?? readInitialPage()
  const locale = (initialPage?.props.locale ??
    (typeof document === 'undefined' ? 'de' : document.documentElement.lang)) as App.Enums.Locale
  const appName = (initialPage?.props.appName ??
    (typeof document === 'undefined' ? 'Frankfurt Plus' : document.title)) as string

  await loadLanguageFile(locale)
  const ziggy = ziggyConfig(initialPage)
  if (ziggy) {
    globalThis.Ziggy = ziggy
  }

  const resolve = (name: string) =>
    resolvePageComponent(
      `../pages/${name}.vue`,
      import.meta.glob<DefineComponent>('../pages/**/*.vue'),
    )
  const setup = ({ el, App, props, plugin }: InertiaSetupOptions) => {
    const app = appFactory({
      render: () => h(App as DefineComponent, props as Record<string, unknown>),
    })
      .use(plugin)
      .component('inertia-link', Link)
      .component('InertiaLink', Link)

    if (ziggy) {
      app.use(ZiggyVue, ziggy)
    } else {
      app.use(ZiggyVue)
    }

    enhance?.(app)

    if (el) {
      app.mount(el)
    }

    return app
  }
  const options = {
    title: (title: string) => `${title} - ${appName}`,
    resolve,
    setup,
  }

  if (render) {
    if (!initialPage) {
      throw new Error('Inertia SSR requires an initial page.')
    }

    return createInertiaApp({ ...options, page: initialPage, render })
  }

  return createInertiaApp({
    ...options,
    page: initialPage,
    progress: {
      color: '#4B5563',
    },
  })
}

function readInitialPage(): Page | undefined {
  if (typeof document === 'undefined') {
    return undefined
  }

  const inlinePage = getInitialPageFromDOM<Page>('app')
  if (inlinePage) {
    return inlinePage
  }

  const page = document.querySelector<HTMLElement>('#app')?.dataset.page

  return page ? (JSON.parse(page) as Page) : undefined
}

function ziggyConfig(page?: Page): ZiggyConfig | undefined {
  if (!page) {
    return undefined
  }

  if (!page.props.ziggy) {
    if (typeof document === 'undefined') {
      throw new TypeError('Inertia SSR requires the Ziggy shared prop.')
    }

    return undefined
  }

  const ziggy = page.props.ziggy as ZiggyConfig

  if (typeof document !== 'undefined') {
    return ziggy
  }

  const location = new URL(page.url, ziggy.url)

  return {
    ...ziggy,
    location: {
      host: location.host,
      pathname: location.pathname,
      search: location.search,
    },
  }
}
